/**
 * Chat — Phase 1 non-streaming chat UI.
 *
 * Two-pane layout:
 * - Left pane: conversation list with search (debounced), sort control,
 *   "New chat" button, and per-row actions (rename inline, archive, delete).
 * - Right pane: full message thread (operator right-aligned, assistant
 *   left-aligned, error bubbles distinctly styled) + composer.
 *
 * On send: optimistically appends the operator message, shows a "working…"
 * indicator while POSTing, then replaces with the real operator message +
 * appends the assistant reply (or error bubble).
 *
 * Auth: uses the existing api client + tokenManager; no new state libraries.
 */
import {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
  useId,
  useLayoutEffect,
  KeyboardEvent,
} from 'react'
import { useSearchParams } from 'react-router-dom'
import { api, UnauthorizedError } from '../api/client'
import type { Conversation, ConversationDetail, ChatMessage, Project, ChatFile } from '../api/client'
// Phase 5 types (RegenerateMessageResponse / EditMessageResponse used inline via api calls)
import { relativeTime } from '../utils/relativeTime'
import { useToast, ToastContainer } from '../utils/toast'
import { ProjectsPanel } from './chat/ProjectsPanel'
import { ChannelsPanel } from './chat/ChannelsPanel'
import { MoveToProjectControl } from './chat/MoveToProjectControl'
import { FileAttachControl } from './chat/FileAttachControl'
import { MentionInput } from './chat/MentionInput'
import { CommandPreviewChip, CommandSuggestionList, useCommandPreview } from './chat/CommandPreview'
import { RunOutput } from './chat/RunOutput'
import { ApprovalCard } from './chat/ApprovalCard'
import { UnreadMentionsBadge } from './chat/UnreadMentionsBadge'
import { useAttentionNotify } from '../context/AttentionContext'
import { useIsNarrow } from '../hooks/useIsNarrow'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function conversationTitle(c: Conversation | ConversationDetail): string {
  return c.title?.trim() || 'Untitled'
}

function useDebounce<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(t)
  }, [value, delayMs])
  return debounced
}

// ---------------------------------------------------------------------------
// Conversation list pane
// ---------------------------------------------------------------------------

interface ConvRowProps {
  conv: Conversation
  active: boolean
  projects: Project[]
  onSelect: () => void
  onArchive: () => void
  onDelete: () => void
  onRename: (title: string) => void
  onTagClick: (tag: string) => void
}

function ConvRow({ conv, active, projects, onSelect, onArchive, onDelete, onRename, onTagClick }: ConvRowProps) {
  const [editing, setEditing] = useState(false)
  const [editValue, setEditValue] = useState(conversationTitle(conv))
  const inputRef = useRef<HTMLInputElement>(null)

  function startEdit(e: React.MouseEvent) {
    e.stopPropagation()
    setEditValue(conversationTitle(conv))
    setEditing(true)
    setTimeout(() => inputRef.current?.select(), 0)
  }

  function commitEdit() {
    const trimmed = editValue.trim()
    if (trimmed && trimmed !== conversationTitle(conv)) {
      onRename(trimmed)
    }
    setEditing(false)
  }

  function handleEditKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') commitEdit()
    if (e.key === 'Escape') setEditing(false)
  }

  const archived = !!conv.archived_at
  const projectName = conv.project_id ? projects.find(p => p.id === conv.project_id)?.name : null

  return (
    <div
      onClick={onSelect}
      className={`group px-3 py-2.5 rounded-lg cursor-pointer transition-colors ${
        active
          ? 'bg-indigo-50 border border-indigo-200'
          : 'hover:bg-gray-100 border border-transparent'
      } ${archived ? 'opacity-60' : ''}`}
    >
      {editing ? (
        <input
          ref={inputRef}
          value={editValue}
          onChange={e => setEditValue(e.target.value)}
          onBlur={commitEdit}
          onKeyDown={handleEditKey}
          onClick={e => e.stopPropagation()}
          className="w-full text-sm font-medium text-gray-900 bg-white border border-indigo-300 rounded px-1.5 py-0.5 outline-none focus:ring-1 focus:ring-indigo-400"
        />
      ) : (
        <p
          className={`text-sm font-medium truncate ${active ? 'text-indigo-700' : conv.kind === 'channel' ? 'text-teal-700' : 'text-gray-800'}`}
          title={conv.kind === 'channel' ? `#${conv.handle ?? ''} ${conversationTitle(conv)}` : conversationTitle(conv)}
        >
          {conv.kind === 'channel' ? (
            <><span className="text-teal-500 font-bold">#</span>{conv.handle ?? conversationTitle(conv)}</>
          ) : conversationTitle(conv)}
        </p>
      )}

      <div className="flex items-center justify-between mt-0.5">
        <div className="flex items-center gap-1.5 min-w-0">
          {conv.primary_agent && (
            <span className="text-xs text-gray-400 font-mono truncate max-w-[90px]" title={conv.primary_agent}>
              {conv.primary_agent}
            </span>
          )}
          {conv.created_by && (
            <span className="text-xs text-gray-400 truncate max-w-[80px]" title={conv.created_by}>
              · {conv.created_by}
            </span>
          )}
        </div>
        <span className="text-xs text-gray-400 shrink-0 ml-1">
          {relativeTime(conv.last_message_at ?? conv.updated_at)}
        </span>
      </div>

      {/* Project badge */}
      {projectName && (
        <div className="mt-0.5">
          <span className="text-xs px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-600 border border-violet-100">
            {projectName}
          </span>
        </div>
      )}

      {/* Tag chips */}
      {conv.tags && conv.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1" onClick={e => e.stopPropagation()}>
          {conv.tags.map(tag => (
            <button
              key={tag}
              onClick={e => { e.stopPropagation(); onTagClick(tag) }}
              className="text-xs px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-600 hover:bg-indigo-100 transition-colors"
            >
              {tag}
            </button>
          ))}
        </div>
      )}

      {/* Row actions — visible on hover/active */}
      <div
        className={`flex items-center gap-1 mt-1 transition-opacity ${
          active ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
        }`}
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={startEdit}
          className="text-xs text-gray-400 hover:text-indigo-600 px-1 py-0.5 rounded hover:bg-indigo-50 transition-colors"
          title="Rename"
        >
          Rename
        </button>
        {!archived && (
          <button
            onClick={e => { e.stopPropagation(); onArchive() }}
            className="text-xs text-gray-400 hover:text-amber-600 px-1 py-0.5 rounded hover:bg-amber-50 transition-colors"
            title="Archive"
          >
            Archive
          </button>
        )}
        <button
          onClick={e => { e.stopPropagation(); onDelete() }}
          className="text-xs text-gray-400 hover:text-red-600 px-1 py-0.5 rounded hover:bg-red-50 transition-colors"
          title="Delete"
        >
          Delete
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Pending work (ADR-124 D4)
// ---------------------------------------------------------------------------

/** "45s", "2m 05s", "1h 04m" — coarse enough not to imply false precision. */
function formatElapsed(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  if (seconds < 3600) {
    const m = Math.floor(seconds / 60)
    return `${m}m ${String(seconds % 60).padStart(2, '0')}s`
  }
  const h = Math.floor(seconds / 3600)
  return `${h}h ${String(Math.floor((seconds % 3600) / 60)).padStart(2, '0')}m`
}

/** Seconds since `iso`, ticking every second. 0 if the timestamp is unusable. */
function useElapsedSeconds(iso: string): number {
  const started = useMemo(() => {
    const t = Date.parse(iso)
    return Number.isNaN(t) ? null : t
  }, [iso])

  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (started === null) return
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [started])

  if (started === null) return 0
  return Math.max(0, Math.floor((now - started) / 1000))
}

/**
 * ADR-124 D4 — whoever is waiting is told what is running.
 *
 * The previous state of this UI was the literal word "working" and three
 * bouncing dots: no agent, no goal, no elapsed time. An acknowledgement with no
 * progress and no bound is the acknowledgment illusion — the operator cannot
 * tell work in flight from work that silently died.
 *
 * Every field here is already persisted on the pending placeholder that
 * deferBackgroundJob creates; none of it needed new plumbing. Anything actually
 * missing is omitted rather than invented, so this degrades to the old
 * behaviour instead of asserting something untrue.
 */
function PendingWork({ message }: { message: ChatMessage }) {
  const elapsed = useElapsedSeconds(message.created_at)
  const agent = message.author_agent
  const goal = message.background_goal

  return (
    <div className="flex flex-col gap-1">
      <span className="flex gap-1.5 items-center">
        <span className="flex gap-0.5" aria-hidden="true">
          {[0, 1, 2].map(i => (
            <span
              key={i}
              className="w-1 h-1 rounded-full bg-gray-400 animate-bounce"
              style={{ animationDelay: `${i * 150}ms` }}
            />
          ))}
        </span>
        <span className="text-xs text-gray-500">
          {agent ? <span className="font-medium text-gray-700">{agent}</span> : 'working'}
          {elapsed > 0 && <span className="text-gray-400"> · {formatElapsed(elapsed)}</span>}
        </span>
      </span>
      {goal && (
        <span className="text-xs text-gray-500 italic leading-snug">{goal}</span>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Message bubble
// ---------------------------------------------------------------------------

interface MessageBubbleProps {
  message: ChatMessage
  optimistic?: boolean
  conversationId?: string
  onRegenerate?: (msg: ChatMessage) => void
  onEdit?: (msg: ChatMessage, newContent: string) => void
  onBranch?: (msg: ChatMessage) => void
  /** ADR-203 — refetch after an approval on this run is decided. */
  onApprovalDecided?: () => void
}

function MessageBubble({ message, optimistic, conversationId, onRegenerate, onEdit, onBranch, onApprovalDecided }: MessageBubbleProps) {
  const isOperator = message.role === 'operator'
  const isError = message.status === 'error'
  const isPending = message.status === 'pending' || message.status === 'streaming'
  const isUnknown = message.status === 'unknown'
  // ADR-203 — a run waiting on an approval: decided on its card, never regenerated.
  const isParked = message.status === 'parked'
  const isSuperseded = !!message.superseded_by

  const [editing, setEditing] = useState(false)
  const [editValue, setEditValue] = useState(message.content)
  const [controlError, setControlError] = useState<string | null>(null)
  const editInputRef = useRef<HTMLInputElement>(null)

  const meta = isOperator
    ? (message.author_email ?? 'you')
    : (message.author_agent ?? 'assistant')

  function startInlineEdit() {
    setEditValue(message.content)
    setEditing(true)
    setTimeout(() => editInputRef.current?.focus(), 0)
  }

  function commitEdit() {
    const trimmed = editValue.trim()
    if (trimmed && trimmed !== message.content && onEdit) {
      onEdit(message, trimmed)
    }
    setEditing(false)
  }

  function handleEditKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') { e.preventDefault(); commitEdit() }
    if (e.key === 'Escape') setEditing(false)
  }

  return (
    <div className={`flex ${isOperator ? 'justify-end' : 'justify-start'} gap-2 group ${isSuperseded ? 'opacity-50' : ''}`}>
      <div className={`max-w-[72%] ${isOperator ? 'order-2' : 'order-1'}`}>
        {isSuperseded && (
          <p className={`text-xs mb-0.5 text-gray-400 italic ${isOperator ? 'text-right' : 'text-left'}`}>
            (superseded)
          </p>
        )}
        <div
          className={`rounded-xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap break-words ${
            isError
              ? 'bg-red-50 border border-red-200 text-red-700'
              : isOperator
                ? 'bg-indigo-600 text-white'
                : 'bg-white border border-gray-200 text-gray-800'
          } ${optimistic ? 'opacity-60' : ''}`}
        >
          {isError && (
            <p className="text-xs font-semibold mb-1 text-red-600">Error from assistant</p>
          )}
          {editing ? (
            <div className="flex gap-2 items-center">
              <input
                ref={editInputRef}
                value={editValue}
                onChange={e => setEditValue(e.target.value)}
                onKeyDown={handleEditKey}
                className="flex-1 text-sm bg-white text-gray-900 border border-indigo-300 rounded px-2 py-1 outline-none focus:ring-1 focus:ring-indigo-400"
              />
              <button
                onClick={commitEdit}
                className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded hover:bg-indigo-200 transition-colors shrink-0"
              >
                Send
              </button>
              <button
                onClick={() => setEditing(false)}
                className="text-xs text-gray-400 hover:text-gray-600 px-1"
              >
                ✕
              </button>
            </div>
          ) : isUnknown ? (
            <div><p>Execution outcome is unknown.</p><button onClick={() => {
              setControlError(null)
              if (conversationId) void api.resolveUnknownRun(conversationId, message.id).then(() => onApprovalDecided?.()).catch(error => setControlError(String(error?.message ?? error)))
            }}>Acknowledge</button></div>
          ) : isPending ? (
            <div><PendingWork message={message} />{!isOperator && <button onClick={() => {
              setControlError(null)
              if (conversationId) void api.stopMessage(conversationId, message.id).then(() => onApprovalDecided?.()).catch(error => setControlError(String(error?.message ?? error)))
            }}>Stop</button>}</div>
          ) : (
            // ADR-160 D1 — an activity run's raw step output is collapsed behind a
            // toggle; a message without the marker renders exactly as before.
            <RunOutput content={message.content} />
          )}
          {controlError && <p role="alert" className="text-xs text-red-600 mt-1">{controlError}</p>}
          {(message.approvals ?? []).map(a => (
            <ApprovalCard key={a.id} approval={a} onDecided={() => onApprovalDecided?.()} />
          ))}
        </div>
        <div className={`flex items-center gap-2 mt-0.5 ${isOperator ? 'justify-end' : 'justify-start'}`}>
          <p className="text-xs text-gray-400">
            {meta} · {relativeTime(message.created_at)}
          </p>
          {/* Message action buttons — shown on bubble hover */}
          {!optimistic && !isSuperseded && !isPending && !isUnknown && !isParked && !editing && (
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {!isOperator && onRegenerate && (
                <button
                  onClick={() => onRegenerate(message)}
                  className="text-xs text-gray-400 hover:text-indigo-600 px-1 py-0.5 rounded hover:bg-indigo-50 transition-colors"
                  title="Regenerate response"
                >
                  ↺ Regenerate
                </button>
              )}
              {isOperator && onEdit && (
                <button
                  onClick={startInlineEdit}
                  className="text-xs text-gray-400 hover:text-indigo-600 px-1 py-0.5 rounded hover:bg-indigo-50 transition-colors"
                  title="Edit and resend"
                >
                  ✎ Edit
                </button>
              )}
              {isOperator && onBranch && (
                <button
                  onClick={() => onBranch(message)}
                  className="text-xs text-gray-400 hover:text-violet-600 px-1 py-0.5 rounded hover:bg-violet-50 transition-colors"
                  title="Branch conversation from here"
                >
                  ⎇ Branch
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function WorkingIndicator() {
  return (
    <div className="flex justify-start">
      <div className="bg-white border border-gray-200 rounded-xl px-3.5 py-2.5">
        <span className="flex gap-1 items-center">
          <span className="text-xs text-gray-400">working</span>
          <span className="flex gap-0.5">
            {[0, 1, 2].map(i => (
              <span
                key={i}
                className="w-1 h-1 rounded-full bg-gray-400 animate-bounce"
                style={{ animationDelay: `${i * 150}ms` }}
              />
            ))}
          </span>
        </span>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// ShareControl — copy a permalink to this conversation
// ---------------------------------------------------------------------------

/**
 * conversationShareURL builds the absolute permalink for a conversation.
 *
 * Deliberately not a share *token*: the link is the plain deep-link URL, and the
 * recipient authenticates as themselves. Every operator holding a console grant
 * for this fleet can already read every conversation in it (neither
 * GET /api/chat/conversations/:id nor the list query scopes by created_by), so
 * handing out this URL grants no access the recipient did not already have —
 * which is why there is no secret to leak and nothing to revoke.
 */
function conversationShareURL(conversationId: string, origin: string): string {
  return `${origin}/chat?conversation=${encodeURIComponent(conversationId)}`
}

function ShareControl({ conversationId }: { conversationId: string }) {
  const [copied, setCopied] = useState(false)
  const resetRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Clear the pending "Copied!" reset if the pane unmounts (switching threads
  // remounts ThreadPane via its key) — otherwise React warns about a state
  // update on an unmounted component.
  useEffect(() => () => {
    if (resetRef.current) clearTimeout(resetRef.current)
  }, [])

  async function copy() {
    const url = conversationShareURL(conversationId, window.location.origin)
    try {
      await navigator.clipboard.writeText(url)
    } catch {
      // navigator.clipboard needs a secure context and can be permission-denied.
      // Fall back to a prompt so the link is still selectable by hand rather
      // than the button silently doing nothing.
      window.prompt('Copy this link to share the conversation:', url)
      return
    }
    setCopied(true)
    if (resetRef.current) clearTimeout(resetRef.current)
    resetRef.current = setTimeout(() => setCopied(false), 2000)
  }

  return (
    <button
      onClick={() => { void copy() }}
      className="text-xs text-gray-500 hover:text-indigo-600 px-2 py-1 rounded border border-gray-200 hover:border-indigo-300 transition-colors shrink-0"
      title="Copy a link to this conversation. Anyone with access to this fleet's Mission Control can open it."
    >
      {copied ? 'Copied!' : 'Share'}
    </button>
  )
}

// ---------------------------------------------------------------------------
// ExportControl — dropdown to export a conversation as Markdown or CSV (R6)
// ---------------------------------------------------------------------------

function ExportControl({ conversationId }: { conversationId: string }) {
  const [open, setOpen] = useState(false)
  const [exporting, setExporting] = useState(false)

  async function doExport(format: 'markdown' | 'csv') {
    setOpen(false)
    setExporting(true)
    try {
      const { blob, filename } = await api.exportConversation(conversationId, format)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch {
      // silently fail (could add toast)
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(v => !v)}
        disabled={exporting}
        className="text-xs text-gray-500 hover:text-indigo-600 px-2 py-1 rounded border border-gray-200 hover:border-indigo-300 transition-colors disabled:opacity-50"
        title="Export conversation"
      >
        {exporting ? 'Exporting…' : 'Export ↓'}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 w-36">
          <button
            onClick={() => void doExport('markdown')}
            className="w-full text-left text-sm px-3 py-2 hover:bg-gray-50 rounded-t-lg"
          >
            Markdown (.md)
          </button>
          <button
            onClick={() => void doExport('csv')}
            className="w-full text-left text-sm px-3 py-2 hover:bg-gray-50 rounded-b-lg border-t border-gray-100"
          >
            CSV (.csv)
          </button>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Thread pane
// ---------------------------------------------------------------------------

const POLL_INTERVAL_MS = 3000
// A specialist's answer arrives after the turn that started it has finished, with
// nothing pending in between, so an open conversation keeps checking for it.
const IDLE_POLL_INTERVAL_MS = 10000

/** Returns true if there is at least one message in pending or streaming state. */
function hasPendingMessages(msgs: ChatMessage[]): boolean {
  return msgs.some(m => m.status === 'pending' || m.status === 'streaming')
}

function isTerminalAssistant(m: ChatMessage): boolean {
  return m.role === 'assistant' && m.status !== 'pending' && m.status !== 'streaming'
}

// ---------------------------------------------------------------------------
// The composer's resizer (Asana 1217944390850589). Ported from the Fleet
// Editor's assistant dock (83c7be00): a drawn, focusable strip on the
// composer's top edge, sized by pointer drag within bounds that keep both the
// transcript and the composer usable.
// ---------------------------------------------------------------------------

/** The textarea floor: one row, matching the existing min-h-[2.75rem] backstop,
 *  because a composer with nothing to type in is not a usable end of a drag. */
const COMPOSER_MIN_PX = 44

/** The transcript's floor: enough to read a reply and the message it answers.
 *  Below this the thread the composer exists to talk about stops being
 *  readable, so the drag stops instead. It is the WEAKER of the two floors:
 *  see composerCeiling, where a region too short for both hands the pixels to
 *  the composer. */
const TRANSCRIPT_MIN_PX = 200

/** Remembered per browser. An operator who wants a tall message box wants it on
 *  every conversation, and ThreadPane remounts on every switch. */
const COMPOSER_HEIGHT_KEY = 'mc.chat.composerHeight'

/** One arrow press. Same step as the Fleet Editor divider this copies, so the
 *  two resizers do not feel different under the same fingers. */
const KEYBOARD_STEP_PX = 24

/** What an operator who never finds the handle gets. A row count rather than a
 *  pixel height, so the default follows the font and the line height. */
const COMPOSER_DEFAULT_ROWS = 4

/** No composer owns more than this share of the window, however tall the
 *  region it sits in happens to be. */
const COMPOSER_MAX_VIEWPORT_SHARE = 0.6

/**
 * The stored preference, or null if there is nothing trustworthy to use.
 *
 * No upper bound is checked here on purpose: the ceiling depends on the current
 * window, so a height chosen on a tall monitor must be clamped down when the
 * operator opens a laptop, not discarded.
 */
function readStoredComposerHeight(): number | null {
  try {
    const raw = window.localStorage.getItem(COMPOSER_HEIGHT_KEY)
    if (!raw) return null
    const n = Number(raw)
    return Number.isFinite(n) && n >= COMPOSER_MIN_PX ? n : null
  } catch {
    // Private mode, or storage disabled. A remembered height is a convenience;
    // losing it must never stop Chat rendering.
    return null
  }
}

/**
 * A requested textarea height, held between the two floors.
 *
 * `chrome` is the composer block minus the textarea (Attach row, gap, padding,
 * border), measured live rather than hardcoded so a block that grows an
 * attachment-chip row still lands on the right height.
 *
 * The viewport-share cap lives here rather than in a max-h class: left as CSS it would
 * trim the rendered height while state and storage kept the larger number.
 *
 * The outer max is a precedence rule, not a rounding detail. In a region too
 * short to hold both floors — a short window, a wrapped tag row in the thread
 * header — one of them has to give, and it is the transcript's: a composer
 * squeezed below one row leaves nothing to type in, which is the worse end.
 * So the transcript keeps TRANSCRIPT_MIN_PX whenever the region allows it, and
 * not unconditionally.
 */
function composerCeiling(regionHeight: number, chrome: number): number {
  return Math.max(
    COMPOSER_MIN_PX,
    Math.min(regionHeight - TRANSCRIPT_MIN_PX - chrome, COMPOSER_MAX_VIEWPORT_SHARE * window.innerHeight),
  )
}

function clampComposerHeight(px: number, regionHeight: number, chrome: number): number {
  return Math.min(Math.max(px, COMPOSER_MIN_PX), composerCeiling(regionHeight, chrome))
}

/**
 * The height the textarea would have with nothing sizing it: its rows.
 *
 * While a clamp is applied, measuring the box returns the clamp, so the rows
 * are unobservable — the height is lifted, read, and put straight back inside
 * one synchronous pass, so nothing is painted in between. Both quantities the
 * default clamp depends on (the rows and the ceiling) are then live, and
 * neither has to be remembered from an earlier pass and go stale.
 */
function measureNaturalTextareaHeight(box: HTMLTextAreaElement): number {
  const applied = box.style.height
  if (!applied) return box.getBoundingClientRect().height
  box.style.height = ''
  const natural = box.getBoundingClientRect().height
  box.style.height = applied
  return natural
}

/** The geometry the resizer reads, kept in state so a render can report it
 *  without touching a ref. */
interface ComposerGeometry {
  regionHeight: number
  /** The composer block minus the textarea: Attach row, gap, padding, border. */
  chrome: number
  /** What the textarea currently measures, which is the truthful aria-valuenow
   *  before the operator has resized anything. */
  textareaHeight: number
}

/** Cheap identity so a reconciliation that measured nothing new does not
 *  re-render — the observer watches the very block this function resizes, and
 *  a fresh object every time makes that a self-triggering loop. */
function sameGeometry(a: ComposerGeometry | null, b: ComposerGeometry): boolean {
  return a !== null
    && a.regionHeight === b.regionHeight
    && a.chrome === b.chrome
    && a.textareaHeight === b.textareaHeight
}

interface ThreadPaneProps {
  conversationId: string
  projects: Project[]
  onConversationUpdated: (conv: Conversation) => void
  onTagClick: (tag: string) => void
  onBranchCreated?: (conv: Conversation) => void
  /** Phone only: return to the conversation list. Absent on a desktop, where it is beside us. */
  onBack?: () => void
}

function ThreadPane({ conversationId, projects, onConversationUpdated, onTagClick, onBranchCreated, onBack }: ThreadPaneProps) {
  const { addToast } = useToast()
  const notifyAttention = useAttentionNotify()
  const [detail, setDetail] = useState<ConversationDetail | null>(null)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const knownTerminalRef = useRef<Set<string>>(new Set())
  const seededRef = useRef(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [composerValue, setComposerValue] = useState('')
  const commandPreview = useCommandPreview(conversationId, composerValue)
  const suggestions = commandPreview?.suggestions ?? null
  const [suggestionsDismissed, setSuggestionsDismissed] = useState(false)
  useEffect(() => {
    setSuggestionsDismissed(false)
  }, [composerValue])
  const suggestionStem = suggestions?.items[0]?.text.replace(/[ \t]+[^ \t]+$/, '') ?? null
  const partialTyped = suggestionStem !== null && composerValue.trim() !== suggestionStem
  const selectedIndex = suggestions?.selected ? suggestions.items.findIndex(item => item.value === suggestions.selected) : -1
  const [highlightedSuggestion, setHighlightedSuggestion] = useState<number | null>(null)
  useEffect(() => {
    setHighlightedSuggestion(selectedIndex >= 0 ? selectedIndex : partialTyped ? 0 : null)
  }, [suggestions, selectedIndex, partialTyped])
  const showSuggestions = Boolean(suggestions && !suggestionsDismissed)
  const [sending, setSending] = useState(false)
  const bottomRef = useRef<HTMLDivElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const composerRef = useRef<HTMLTextAreaElement>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  // pollRef's running/stopped as state, for render: a ref read during render neither
  // re-renders when it changes nor is safe under the React compiler.
  const [polling, setPolling] = useState(false)
  // Sticky-bottom autoscroll: only follow new content when the operator is
  // already pinned to the bottom. If they've scrolled up to read history (e.g.
  // while a reply is being polled), we must NOT yank them back down.
  const atBottomRef = useRef(true)
  // First scroll into a freshly-opened conversation jumps instantly; subsequent
  // updates animate. Avoids a long smooth-scroll from the top on open.
  const didInitialScrollRef = useRef(false)
  // Drives the "Jump to latest" affordance shown when scrolled away from bottom.
  const [showJumpButton, setShowJumpButton] = useState(false)
  const [tags, setTags] = useState<string[]>([])
  const [tagInput, setTagInput] = useState('')
  const [tagging, setTagging] = useState(false)
  const [attachedFiles, setAttachedFiles] = useState<ChatFile[]>([])

  // TWO heights, not one.
  //
  // The preference is what the operator chose and the only thing localStorage
  // holds; it lives in a ref because nothing renders it directly and a clamp
  // must never overwrite it. Seeded from storage so it survives the
  // `key={activeId}` remount that throws a native corner drag away today.
  // Seeded in a layout effect below rather than here, because a drag cut short
  // by that very remount writes storage from the OLD pane's unmount — which
  // React runs after this pane has already rendered.
  const preferredHeightRef = useRef<number | null>(null)
  // What actually goes on the textarea: the preference clamped against the
  // region measured NOW. So a height chosen on a monitor narrows on a laptop
  // and comes back in full when the window grows again.
  const [renderedHeight, setRenderedHeight] = useState<number | null>(null)
  // The same value, readable synchronously: a pointermove commits before the
  // state it just set has re-rendered.
  const renderedHeightRef = useRef<number | null>(null)
  // The last measured geometry. Held in state rather than read from the refs
  // during render, both because refs are not readable there and because the
  // separator's aria-valuemin/max/now have to be numbers React can re-render.
  const [composerGeometry, setComposerGeometry] = useState<ComposerGeometry | null>(null)
  // The two ends of the resizable region: the transcript's top and the
  // composer block's bottom. NOT the column — it also holds a shrink-0 thread
  // header whose tag row wraps, so its height is variable and counting it as
  // transcript would leave the thread below its floor.
  const transcriptRef = useRef<HTMLDivElement>(null)
  const composerBlockRef = useRef<HTMLDivElement>(null)
  // A one-line box carrying the textarea's font and nothing else. It exists
  // because of what the clamp does to the two refs above: while an inline
  // height pins the textarea, the block is pinned by the textarea and the
  // transcript by the block, so a change in ROW height — a web font swapping
  // in after first paint, a browser text-size change — resizes none of them
  // and the observer never fires. This box is out of flow, so nothing the
  // reconciliation does moves it, and its height tracks exactly the font
  // metrics the row height is made of.
  const rowMetricsRef = useRef<HTMLSpanElement>(null)
  // The separator's aria-controls target, so the handle names what it sizes.
  const composerBlockId = useId()
  // The pointer that owns the divider, or null. setPointerCapture retargets
  // every later pointer event to the handle, so React's own props on that
  // element see exactly what a hand-wired native listener would.
  const dragPointerRef = useRef<number | null>(null)
  // Where the gesture started, so the drag is a DELTA. An absolute
  // "bottom - clientY" jumps the height by wherever inside the strip the
  // pointer landed.
  const dragStartYRef = useRef(0)
  const dragStartHeightRef = useRef(0)

  /**
   * Read the live geometry, or null when the tree is not measurable yet.
   *
   * `chrome` is taken as block minus textarea, so it is independent of the
   * textarea's own height and the arithmetic is not circular.
   */
  const measureComposerGeometry = useCallback((): ComposerGeometry | null => {
    const transcript = transcriptRef.current
    const block = composerBlockRef.current
    const box = composerRef.current
    if (!transcript || !block || !box) return null
    const blockRect = block.getBoundingClientRect()
    const textareaHeight = box.getBoundingClientRect().height
    return {
      regionHeight: blockRect.bottom - transcript.getBoundingClientRect().top,
      chrome: blockRect.height - textareaHeight,
      textareaHeight,
    }
  }, [])

  /** Mirror every rendered height into its ref, and skip the render entirely
   *  when nothing moved. */
  const applyRenderedHeight = useCallback((next: number | null) => {
    if (renderedHeightRef.current === next) return
    renderedHeightRef.current = next
    setRenderedHeight(next)
  }, [])

  /** One writer for the measured geometry, so the cheap-identity guard cannot
   *  drift between the reconcile path and the commit path. */
  const storeGeometry = useCallback((geometry: ComposerGeometry) => {
    setComposerGeometry(prev => (sameGeometry(prev, geometry) ? prev : geometry))
  }, [])

  /** The preference reaches storage here and nowhere else, so a pointermove
   *  does not write synchronously dozens of times a second. */
  const persistComposerHeight = useCallback(() => {
    const preferred = preferredHeightRef.current
    if (preferred === null) return
    try {
      window.localStorage.setItem(COMPOSER_HEIGHT_KEY, String(preferred))
    } catch {
      // Private mode, or storage disabled. The height still applies for this
      // session; only remembering it is lost.
    }
  }, [])

  /**
   * Re-clamp the preference against the layout as it is right now.
   *
   * This is the only path that changes the rendered height without an operator
   * gesture, and it deliberately does NOT touch the preference or storage: a
   * shorter window is a temporary fact about the screen, not a new choice.
   */
  const reconcileComposerHeight = useCallback(() => {
    const geometry = measureComposerGeometry()
    if (geometry === null) return
    storeGeometry(geometry)
    const preferred = preferredHeightRef.current
    if (preferred !== null) {
      applyRenderedHeight(clampComposerHeight(preferred, geometry.regionHeight, geometry.chrome))
      return
    }
    // No preference, so the box is row-sized — but the ceiling still has to
    // bind, or on a short window the default rows push the transcript below
    // TRANSCRIPT_MIN_PX, which is the one thing the ceiling exists to prevent.
    // Both sides are measured on this pass: the rows can shrink under a ceiling
    // that never moved (a web font swapping in), and the ceiling can move under
    // rows that never changed, and either has to release or reapply the clamp.
    const box = composerRef.current
    if (box === null) return
    const natural = measureNaturalTextareaHeight(box)
    const ceiling = composerCeiling(geometry.regionHeight, geometry.chrome)
    applyRenderedHeight(natural > ceiling ? ceiling : null)
  }, [measureComposerGeometry, storeGeometry, applyRenderedHeight])

  /** The one commit path: the drag and the arrow keys both land here, so the
   *  clamp and the reported value cannot drift apart. A gesture is the only
   *  thing that moves the preference, so it is the only thing that writes. */
  const commitComposerHeight = useCallback((px: number, geometry: ComposerGeometry, persist: boolean) => {
    const next = clampComposerHeight(px, geometry.regionHeight, geometry.chrome)
    storeGeometry(geometry)
    const preferred = preferredHeightRef.current
    // What the box shows against the geometry measured NOW, which is not
    // necessarily renderedHeightRef: the region can shrink before the
    // ResizeObserver delivers, leaving that ref holding a clamp that no longer
    // exists. Deciding against the stale value is how a laptop-sized clamp
    // replaced a monitor-sized preference.
    const shown = clampComposerHeight(
      preferred ?? renderedHeightRef.current ?? geometry.textareaHeight,
      geometry.regionHeight,
      geometry.chrome,
    )
    // A gesture that leaves the box where it already was is not a choice —
    // whether the bounds swallowed it or the pointer never moved. Recording it
    // would either rewrite a larger preference down to this screen's clamp or,
    // with no preference at all, invent one and cost the row-based default
    // permanently. A click on the strip to focus it delivers exactly this: a
    // pointerdown and a pointermove at the same clientY. Reconciling rather
    // than applying `next` is what keeps the row-based default a default: with
    // no preference the box has no pixel height to be pinned at.
    if (next === shown) {
      reconcileComposerHeight()
      return
    }
    preferredHeightRef.current = next
    applyRenderedHeight(next)
    if (persist) persistComposerHeight()
  }, [persistComposerHeight, storeGeometry, applyRenderedHeight, reconcileComposerHeight])

  /** The height a gesture starts from. The ref, not the state: auto-repeat
   *  delivers several keydowns, and a pointermove commits, before the state
   *  they just set has re-rendered — a stale anchor collapses those into one.
   *  Before anything has been resized there is no rendered height, so the
   *  measured box is the anchor. */
  function currentComposerHeight(geometry: ComposerGeometry): number {
    return renderedHeightRef.current ?? geometry.textareaHeight
  }

  /** ArrowUp grows, ArrowDown shrinks. Anything else is left to the browser so
   *  Tab can still move focus off the handle. */
  function handleResizerKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return
    const geometry = measureComposerGeometry()
    if (geometry === null) return
    e.preventDefault()
    const current = currentComposerHeight(geometry)
    const step = e.key === 'ArrowUp' ? KEYBOARD_STEP_PX : -KEYBOARD_STEP_PX
    commitComposerHeight(current + step, geometry, true)
  }

  /**
   * Resize by dragging the divider. Pointer events (not mouse) so a trackpad, a
   * touchscreen and a stylus all work, and pointer CAPTURE so a fast drag does
   * not detach from a 10px strip.
   */
  function startDrag(e: React.PointerEvent<HTMLDivElement>) {
    e.preventDefault()
    const geometry = measureComposerGeometry()
    if (geometry === null) return
    try {
      e.currentTarget.setPointerCapture(e.pointerId)
    } catch {
      // A stale or invalid pointer id raises NotFoundError. Arming the drag
      // anyway would leave it armed forever: with no capture a pointerup off
      // the 10px strip never reaches endDrag and lostpointercapture never
      // fires, so the next hover with no button down would resize the box.
      return
    }
    // preventDefault above suppresses the default focus, and the arrow keys
    // the handle's own title advertises only reach a focused handle.
    e.currentTarget.focus()
    dragPointerRef.current = e.pointerId
    dragStartYRef.current = e.clientY
    dragStartHeightRef.current = currentComposerHeight(geometry)
  }

  function dragMove(e: React.PointerEvent<HTMLDivElement>) {
    // A second finger during a drag must not drive the resize.
    if (dragPointerRef.current !== e.pointerId) return
    // Measured every move, so an attachment-chip row is accounted for the
    // moment it appears.
    const geometry = measureComposerGeometry()
    if (geometry === null) return
    commitComposerHeight(dragStartHeightRef.current + (dragStartYRef.current - e.clientY), geometry, false)
  }

  // The stored preference is read HERE, not during render, and before the
  // reconciliation below clamps anything. A drag cut short by the thread switch
  // writes storage from the outgoing pane's unmount, which React runs in the
  // same commit as this pane's render but after it — so a render-time read
  // misses exactly the value the switch was supposed to preserve.
  useLayoutEffect(() => {
    preferredHeightRef.current = readStoredComposerHeight()
  }, [])

  // Unmount is the fourth end of a drag, and the only one that delivers no
  // pointer event: ThreadPane is keyed on the conversation, so switching thread
  // mid-gesture detaches the handlers that would have written the height. The
  // operator dragged to a visible height, so that height is a choice and has to
  // reach storage. A layout effect, because a passive cleanup runs after paint —
  // too late for the incoming pane to read what it wrote.
  useLayoutEffect(() => () => {
    if (dragPointerRef.current !== null) persistComposerHeight()
  }, [persistComposerHeight])

  // ONE idempotent teardown, reached from pointerup, pointercancel and
  // lostpointercapture. pointercancel fires instead of pointerup when the
  // browser claims the gesture; lostpointercapture when capture is taken away.
  function endDrag(e: React.PointerEvent<HTMLDivElement>) {
    if (dragPointerRef.current !== e.pointerId) return
    dragPointerRef.current = null
    try {
      e.currentTarget.releasePointerCapture(e.pointerId)
    } catch {
      // Capture was already gone — that is the case this teardown exists for.
    }
    // Storage is written once per gesture, not once per pointermove.
    persistComposerHeight()
  }

  // Refs are not measurable inside useState's initialiser, so the first clamp
  // has to happen after the tree exists and before paint — otherwise the
  // operator sees one frame of an oversized composer. Keyed on `loading`
  // because the composer does not exist during the thread's loading state, so
  // a mount-only effect would measure nothing and never run again. `error`
  // gates the composer through its own early return, so it belongs in the
  // deps too rather than relying on setError(null) always riding along with
  // the loading cycle.
  useLayoutEffect(() => {
    reconcileComposerHeight()
  }, [reconcileComposerHeight, loading, error])

  // The geometry also moves without a render: the window resizes, the header's
  // tag row wraps, an attachment chip appears or leaves.
  useEffect(() => {
    const transcript = transcriptRef.current
    const block = composerBlockRef.current
    if (transcript === null || block === null) return
    // Guarded the way FieldCaption guards its own: jsdom has no
    // ResizeObserver, and losing the observer only costs the reconciliation
    // between renders — the layout effect above still clamps.
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => { reconcileComposerHeight() })
    observer.observe(transcript)
    observer.observe(block)
    // The third box is not redundant: it is the only observed one whose size
    // still moves once a clamp has pinned the other two.
    const rowMetrics = rowMetricsRef.current
    if (rowMetrics !== null) observer.observe(rowMetrics)
    return () => { observer.disconnect() }
  }, [reconcileComposerHeight, loading, error])

  // Stop any running poll.
  const stopPoll = useCallback(() => {
    if (pollRef.current !== null) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
    setPolling(false)
  }, [])

  // Start polling getConversation every POLL_INTERVAL_MS.
  const startPoll = useCallback(() => {
    if (pollRef.current !== null) return
    setPolling(true)
    pollRef.current = setInterval(async () => {
      try {
        const d = await api.getConversation(conversationId)
        const msgs = Array.isArray(d.messages) ? d.messages : []
        setMessages(msgs)
        if (!hasPendingMessages(msgs)) {
          stopPoll()
        }
      } catch {
        // swallow poll errors silently
      }
    }, POLL_INTERVAL_MS)
  }, [conversationId, stopPoll])

  // Pick up replies that arrive once nothing is pending. startPoll takes over while
  // something is, and a send in flight owns the list until its own response lands.
  useEffect(() => {
    if (sending) return
    let cancelled = false
    const id = setInterval(async () => {
      if (pollRef.current !== null) return
      try {
        const d = await api.getConversation(conversationId)
        if (cancelled || pollRef.current !== null) return
        const msgs = Array.isArray(d.messages) ? d.messages : []
        // A failed send exists only here, and it holds the operator's text.
        setMessages(prev => [...msgs, ...prev.filter(m => m.id.startsWith('err-'))])
        if (hasPendingMessages(msgs)) startPoll()
      } catch {
        // swallow poll errors silently
      }
    }, IDLE_POLL_INTERVAL_MS)
    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [conversationId, sending, startPoll])

  // Fetch conversation + messages + files on mount / conversation change.
  // Also mark any mentions in this conversation as read.
  useEffect(() => {
    let cancelled = false
    stopPoll()
    knownTerminalRef.current = new Set()
    seededRef.current = false
    setLoading(true)
    setError(null)
    setDetail(null)
    setMessages([])
    setTags([])
    setAttachedFiles([])

    Promise.all([
      api.getConversation(conversationId),
      api.listFiles(conversationId).catch(() => [] as ChatFile[]),
    ]).then(([d, files]) => {
      if (cancelled) return
      setDetail(d)
      const msgs = Array.isArray(d.messages) ? d.messages : []
      knownTerminalRef.current = new Set(
        msgs
          .filter(isTerminalAssistant)
          .map(message => message.id),
      )
      seededRef.current = true
      setMessages(msgs)
      setTags(Array.isArray(d.tags) ? d.tags : [])
      setAttachedFiles(files)
      if (hasPendingMessages(msgs)) {
        startPoll()
      }
      // Best-effort: mark any @mentions in this conversation as read.
      api.markMentionsRead(conversationId).catch(() => {})
    }).catch(err => {
      if (cancelled) return
      if (err instanceof UnauthorizedError) {
        setError('Session expired. Please re-authenticate.')
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load conversation.')
      }
    }).finally(() => {
      if (!cancelled) setLoading(false)
    })

    return () => {
      cancelled = true
      stopPoll()
    }
  }, [conversationId, startPoll, stopPoll])

  // Seeded history is not new attention. Once the initial read has completed,
  // alert only for assistant messages that newly become terminal.
  useEffect(() => {
    if (!seededRef.current) return
    for (const message of messages) {
      if (!isTerminalAssistant(message)) continue
      if (knownTerminalRef.current.has(message.id)) continue
      knownTerminalRef.current.add(message.id)
      notifyAttention({
        id: `chat:${conversationId}:${message.id}`,
        source: 'chat',
        reason: 'new-reply',
      })
    }
  }, [messages, conversationId, notifyAttention])

  // Recompute whether the operator is pinned to the bottom of the thread.
  const updateAtBottom = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight
    const atBottom = distance < 80
    atBottomRef.current = atBottom
    setShowJumpButton(!atBottom)
  }, [])

  // Pin back to the bottom (used by autoscroll and the Jump-to-latest button).
  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    bottomRef.current?.scrollIntoView({ behavior })
    atBottomRef.current = true
    setShowJumpButton(false)
  }, [])

  // Follow new content only while pinned to the bottom; never override the
  // operator's manual scroll-up (issue: poll ticks were bouncing them down).
  useEffect(() => {
    if (!atBottomRef.current) return
    bottomRef.current?.scrollIntoView({ behavior: didInitialScrollRef.current ? 'smooth' : 'auto' })
    if (messages.length > 0) didInitialScrollRef.current = true
  }, [messages, sending])

  // Focus composer on open.
  useEffect(() => {
    composerRef.current?.focus()
  }, [conversationId])

  const handleSend = useCallback(async () => {
    const content = composerValue.trim()
    if (!content || sending) return

    setComposerValue('')
    setSending(true)
    // Sending is an explicit intent to follow the conversation — re-pin to bottom.
    atBottomRef.current = true
    setShowJumpButton(false)

    const optimisticMsg: ChatMessage = {
      id: `optimistic-${Date.now()}`,
      role: 'operator',
      author_email: null,
      author_agent: null,
      content,
      status: 'ok',
      created_at: new Date().toISOString(),
    }
    setMessages(prev => [...prev, optimisticMsg])

    try {
      const result = await api.sendMessage(conversationId, content)
      setMessages(prev => [
        ...prev.filter(m => m.id !== optimisticMsg.id),
        result.operator_message,
        result.assistant_message,
      ])
      onConversationUpdated({
        ...(detail ?? {} as Conversation),
        id: conversationId,
        last_message_at: result.assistant_message.created_at,
        updated_at: result.assistant_message.created_at,
      } as Conversation)
      startPoll()
    } catch (err) {
      setMessages(prev => prev.filter(m => m.id !== optimisticMsg.id))
      const errMsg = err instanceof Error ? err.message : 'Send failed.'
      const errBubble: ChatMessage = {
        id: `err-${Date.now()}`,
        role: 'assistant',
        author_email: null,
        author_agent: null,
        content: errMsg,
        status: 'error',
        created_at: new Date().toISOString(),
      }
      setMessages(prev => [...prev, errBubble])
    } finally {
      setSending(false)
      composerRef.current?.focus()
    }
  }, [composerValue, sending, conversationId, detail, onConversationUpdated, startPoll])

  function handleComposerKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (showSuggestions && suggestions) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        const current = highlightedSuggestion
        setHighlightedSuggestion(current === null
          ? (e.key === 'ArrowDown' ? 0 : suggestions.items.length - 1)
          : (current + (e.key === 'ArrowDown' ? 1 : -1) + suggestions.items.length) % suggestions.items.length)
        return
      }
      if ((e.key === 'Tab' || (e.key === 'Enter' && !e.shiftKey)) && highlightedSuggestion !== null) {
        const item = suggestions.items[highlightedSuggestion]
        if (item && (e.key === 'Tab' || item.text !== composerValue.trim())) {
          e.preventDefault()
          setComposerValue(item.text)
          composerRef.current?.focus()
          return
        }
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        setSuggestionsDismissed(true)
        return
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void handleSend()
    }
  }

  const handleAddTag = useCallback(async () => {
    const tag = tagInput.trim()
    if (!tag || tagging) return
    setTagging(true)
    try {
      const result = await api.addTag(conversationId, tag)
      setTags(result.tags ?? [])
      setTagInput('')
    } catch {
      // ignore tag errors silently
    } finally {
      setTagging(false)
    }
  }, [tagInput, tagging, conversationId])

  const handleRemoveTag = useCallback(async (tag: string) => {
    try {
      const result = await api.removeTag(conversationId, tag)
      setTags(result.tags ?? [])
    } catch {
      // ignore tag errors silently
    }
  }, [conversationId])

  const handleMoveToProject = useCallback(async (projectId: string | null) => {
    try {
      await api.updateConversation(conversationId, { project_id: projectId })
      setDetail(prev => prev ? { ...prev, project_id: projectId } : prev)
      onConversationUpdated({
        ...(detail ?? {} as Conversation),
        id: conversationId,
        project_id: projectId,
      } as Conversation)
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Move failed.', 'error')
    }
  }, [conversationId, detail, onConversationUpdated, addToast])

  const handleFileUpload = useCallback(async (file: File) => {
    const uploaded = await api.uploadFile(conversationId, file)
    setAttachedFiles(prev => [...prev, uploaded])
  }, [conversationId])

  const handleFileRemove = useCallback(async (fileId: string) => {
    try {
      await api.deleteFile(conversationId, fileId)
      setAttachedFiles(prev => prev.filter(f => f.id !== fileId))
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Remove failed.', 'error')
    }
  }, [conversationId, addToast])

  // -------------------------------------------------------------------------
  // Phase 5 — Message actions: regenerate, edit, branch
  // -------------------------------------------------------------------------

  const handleRegenerate = useCallback(async (msg: ChatMessage) => {
    try {
      const result = await api.regenerateMessage(conversationId, msg.id)
      setMessages(prev =>
        prev
          .map(m => m.id === result.superseded_id ? { ...m, superseded_by: result.assistant_message.id } : m)
          .concat(result.assistant_message),
      )
      startPoll()
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Regenerate failed.', 'error')
    }
  }, [conversationId, startPoll, addToast])

  const handleEdit = useCallback(async (msg: ChatMessage, newContent: string) => {
    try {
      const result = await api.editMessage(conversationId, msg.id, newContent)
      setMessages(prev => {
        const updated = prev.map(m => {
          if (m.id === result.superseded_operator_id) return { ...m, superseded_by: result.operator_message.id }
          if (result.superseded_assistant_id && m.id === result.superseded_assistant_id) return { ...m, superseded_by: result.assistant_message.id }
          return m
        })
        return [...updated, result.operator_message, result.assistant_message]
      })
      startPoll()
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Edit failed.', 'error')
    }
  }, [conversationId, startPoll, addToast])

  // ADR-203 — a decision moves the parked run back to pending, so poll it to
  // completion; a refused decision still refreshes the card to its real state.
  const handleApprovalDecided = useCallback(async () => {
    try {
      const d = await api.getConversation(conversationId)
      const msgs = Array.isArray(d.messages) ? d.messages : []
      setMessages(msgs)
      if (hasPendingMessages(msgs)) startPoll()
    } catch {
      startPoll()
    }
  }, [conversationId, startPoll])

  const handleBranch = useCallback(async (msg: ChatMessage) => {
    try {
      const newConv = await api.branchConversation(conversationId, msg.id)
      // Notify parent so it can add the new conversation to the list and open it.
      onConversationUpdated(newConv as unknown as Conversation)
      if (onBranchCreated) onBranchCreated(newConv as unknown as Conversation)
      addToast('Branched conversation created.', 'success')
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Branch failed.', 'error')
    }
  }, [conversationId, onConversationUpdated, onBranchCreated, addToast])

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-sm text-gray-400 animate-pulse">Loading conversation...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center px-6">
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 max-w-sm text-center">
          {error}
        </div>
      </div>
    )
  }


  // A focusable separator is a range widget under WAI-ARIA, so the implicit
  // 0-100 range is wrong for pixels. Both bounds come from the live clamp
  // rather than constants, so what is announced is what the operator can
  // actually reach; before anything is resized the truthful current value is
  // the textarea's own measured height.
  const composerAriaMax = composerGeometry === null
    ? COMPOSER_MIN_PX
    : Math.round(composerCeiling(composerGeometry.regionHeight, composerGeometry.chrome))
  // Held inside the announced range: before the first measurement the max
  // falls back to the floor, and a value outside min..max is not a range a
  // screen reader can make sense of.
  const composerAriaNow = Math.min(
    composerAriaMax,
    Math.max(
      COMPOSER_MIN_PX,
      Math.round(renderedHeight ?? composerGeometry?.textareaHeight ?? COMPOSER_MIN_PX),
    ),
  )

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Thread header */}
      <div className="px-3 py-2 md:px-5 md:py-3 border-b border-gray-200 shrink-0 bg-white">
        <div className="flex items-center justify-between gap-2">
          {/* On a phone the list and the thread are two SCREENS, not two panes, so this is
              the only way back to the conversation list — there is no list beside it to
              click. Absent on a desktop, where both are on screen at once. */}
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              aria-label="Back to conversations"
              data-testid="thread-back"
              className="shrink-0 -ml-1 p-2 rounded-md text-gray-500 hover:bg-gray-100 active:bg-gray-200 transition-colors"
            >
              <span aria-hidden="true">‹</span>
            </button>
          )}
          <p className="text-sm font-semibold text-gray-900 truncate">
            {detail ? conversationTitle(detail) : '—'}
          </p>
          <div className="flex items-center gap-1.5 shrink-0">
            <ShareControl conversationId={conversationId} />
            <ExportControl conversationId={conversationId} />
          </div>
        </div>
        {detail?.primary_agent && (
          <p className="text-xs text-gray-400 font-mono">{detail.primary_agent}</p>
        )}
        {/* Move to project */}
        {projects.length > 0 && (
          <div className="mt-1">
            <MoveToProjectControl
              currentProjectId={detail?.project_id}
              projects={projects}
              onMove={projectId => { void handleMoveToProject(projectId) }}
            />
          </div>
        )}
        {/* Tag management */}
        <div className="flex flex-wrap items-center gap-1 mt-1.5">
          {tags.map(tag => (
            <span
              key={tag}
              className="inline-flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-600"
            >
              <button
                onClick={() => onTagClick(tag)}
                className="hover:underline"
                title={`Filter by ${tag}`}
              >
                {tag}
              </button>
              <button
                onClick={() => { void handleRemoveTag(tag) }}
                className="text-indigo-400 hover:text-red-500 ml-0.5 leading-none"
                title="Remove tag"
              >
                ×
              </button>
            </span>
          ))}
          <div className="flex items-center gap-1">
            <input
              type="text"
              value={tagInput}
              onChange={e => setTagInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void handleAddTag() } }}
              placeholder="Add tag…"
              className="text-xs border border-gray-200 rounded px-1.5 py-0.5 w-20 focus:outline-none focus:ring-1 focus:ring-indigo-400"
            />
            <button
              onClick={() => { void handleAddTag() }}
              disabled={!tagInput.trim() || tagging}
              className="text-xs text-indigo-600 hover:text-indigo-800 disabled:opacity-40"
            >
              +
            </button>
          </div>
        </div>
      </div>

      {/* Messages — scroll is contained here; the page itself never scrolls. */}
      <div ref={transcriptRef} data-testid="chat-transcript" className="relative flex-1 min-h-0 flex flex-col">
        <div
          ref={scrollRef}
          onScroll={updateAtBottom}
          className="flex-1 overflow-y-auto px-3 py-3 md:px-5 md:py-4 space-y-4 bg-gray-50"
        >
          {messages.length === 0 && !sending && !polling && (
            <p className="text-sm text-gray-400 text-center py-8">
              No messages yet. Say something to start the conversation.
            </p>
          )}
          {messages.map(msg => (
            <MessageBubble
              key={msg.id}
              message={msg}
              optimistic={msg.id.startsWith('optimistic-')}
              conversationId={conversationId}
              onRegenerate={handleRegenerate}
              onEdit={handleEdit}
              onBranch={handleBranch}
              onApprovalDecided={handleApprovalDecided}
            />
          ))}
          {sending && <WorkingIndicator />}
          <div ref={bottomRef} />
        </div>

        {/* Jump-to-latest — appears only when scrolled away from the bottom. */}
        {showJumpButton && (
          <button
            onClick={() => scrollToBottom()}
            className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-full bg-gray-900/80 text-white text-xs font-medium shadow-lg hover:bg-gray-900 transition-colors flex items-center gap-1"
          >
            Jump to latest <span aria-hidden="true">↓</span>
          </button>
        )}
      </div>

      {/* Composer */}
      <div
        ref={composerBlockRef}
        id={composerBlockId}
        data-testid="chat-composer-block"
        // `flex flex-col gap-2`, not `space-y-2`: space-y margins the second
        // and later children, so inserting the separator as the first child
        // would push the attach row down 8px and inflate the measured chrome.
        // Absolutely positioned children do not take part in flex layout, so
        // the separator does not consume a gap.
        className="relative shrink-0 border-t border-gray-200 bg-white px-3 py-2 md:px-4 md:py-3 flex flex-col gap-2"
      >
        {/* ABSOLUTE and stacked, not a static child with a negative margin.
            83c7be00 measured what that costs: with no layout height the strip is
            painted in the band belonging to the pane above, CSS paints
            positioned descendants after static ones, and the neighbour takes the
            pointerdown — one of six pixel rows was actually reachable. The band
            straddles the visible border so the target is where the eye already
            is, and touch-none stops a tablet claiming the vertical drag as a
            scroll before the handler ever sees it. */}
        <div
          role="separator"
          aria-orientation="horizontal"
          aria-label="Resize the message box"
          aria-controls={composerBlockId}
          aria-valuemin={COMPOSER_MIN_PX}
          aria-valuemax={composerAriaMax}
          aria-valuenow={composerAriaNow}
          tabIndex={0}
          onPointerDown={startDrag}
          onPointerMove={dragMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onLostPointerCapture={endDrag}
          onKeyDown={handleResizerKeyDown}
          data-testid="chat-composer-resizer"
          title="Drag to resize — or use the arrow keys"
          /* Pointer-only. It is a 10px drag target sitting across the composer's top edge
             with `touch-none`, which on a phone is a strip of the transcript that silently
             refuses to scroll — and resizing a composer that already fills the screen buys
             nothing there. */
          className="absolute left-0 right-0 -top-2 h-2.5 z-10 cursor-row-resize group touch-none
                     hidden md:flex items-center justify-center
                     hover:bg-indigo-100/70 focus:bg-indigo-100 focus:outline-none"
        >
          <span className="h-1 w-10 rounded-full bg-gray-300 group-hover:bg-indigo-400" />
        </div>
        {/* The row-height probe the observer watches. `absolute` so it takes
            no part in the flex layout and no reconciliation can resize it,
            `invisible` rather than `hidden` because a display:none box has no
            size for a ResizeObserver to report, and `text-sm` because that is
            the textarea's own type scale — the metric being tracked. */}
        <span
          ref={rowMetricsRef}
          aria-hidden="true"
          data-testid="chat-composer-row-metrics"
          className="pointer-events-none invisible absolute left-0 top-0 text-sm"
        >
          &nbsp;
        </span>
        <FileAttachControl
          files={attachedFiles}
          onUpload={handleFileUpload}
          onRemove={fileId => { void handleFileRemove(fileId) }}
          disabled={sending}
        />
        {commandPreview?.preview && <CommandPreviewChip preview={commandPreview.preview} />}
        {showSuggestions && suggestions && <div className="absolute bottom-full left-4 right-4 z-20 mb-2">
          <CommandSuggestionList suggestions={suggestions} highlighted={highlightedSuggestion}
            onPick={text => { setComposerValue(text); composerRef.current?.focus() }} />
        </div>}
        <div className="flex items-end gap-2">
          <MentionInput
            value={composerValue}
            onChange={setComposerValue}
            onKeyDown={handleComposerKeyDown}
            disabled={sending}
            placeholder="Type a message… (@email to mention, Enter to send, Shift+Enter for newline)"
            rows={COMPOSER_DEFAULT_ROWS}
            textareaRef={composerRef}
            style={renderedHeight === null ? undefined : { height: renderedHeight }}
            // `resize-none`, not `resize-y`: the native corner grip sets an
            // inline height React does not know about, which the next state
            // update stamps over. The separator above is the one way to size
            // this box. `min-h-[2.75rem]` is not redundant with the JS floor —
            // it holds before the operator has resized anything, when there is
            // no inline height at all. The old `max-h-[60vh]` is gone on
            // purpose: it lives in clampComposerHeight now, because as CSS it
            // would trim the rendered height while state, storage and
            // aria-valuenow kept the larger number.
            className="w-full resize-none min-h-[2.75rem] rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent disabled:opacity-50 transition-colors"
          />
          {/* A 44px circle on a phone, the full "Send" button everywhere else. As a text
              button it was ~70px of a 412px row that the message box had to give up on every
              screen, and `Enter` already sends — on a phone the control is a target, not a
              label. The glyph is an arrow rather than a paper plane because this box also
              holds multi-line drafts, and up-means-send is the convention there. */}
          <button
            onClick={() => { void handleSend() }}
            disabled={!composerValue.trim() || sending}
            aria-label="Send"
            data-testid="chat-send"
            className="shrink-0 grid place-items-center h-11 w-11 rounded-full md:h-auto md:w-auto md:px-4 md:py-2 md:rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <span className="hidden md:inline">Send</span>
            <svg
              className="md:hidden"
              width="20" height="20" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="12" y1="19" x2="12" y2="5" />
              <polyline points="5 12 12 5 19 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Empty state for no selection
// ---------------------------------------------------------------------------

function NoSelectionPane() {
  return (
    <div className="flex-1 flex items-center justify-center text-center px-8">
      <div>
        <p className="text-gray-400 text-sm font-medium">Select a conversation</p>
        <p className="text-gray-300 text-xs mt-1">or create a new one to get started</p>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Chat page
// ---------------------------------------------------------------------------

export function Chat() {
  const { toasts, addToast } = useToast()

  const [conversations, setConversations] = useState<Conversation[]>([])
  const [loadingList, setLoadingList] = useState(true)
  const [listError, setListError] = useState<string | null>(null)

  const [searchRaw, setSearchRaw] = useState('')
  const search = useDebounce(searchRaw, 300)
  const [sort, setSort] = useState<'name' | 'created' | 'activity'>('activity')

  // Filter state
  const [filterKind, setFilterKind] = useState('')
  const [filterProjectId, setFilterProjectId] = useState('')
  const [filterCreatorRaw, setFilterCreatorRaw] = useState('')
  const filterCreator = useDebounce(filterCreatorRaw, 300)
  const [filterTag, setFilterTag] = useState('')

  const [projects, setProjects] = useState<Project[]>([])
  const [showProjectsPanel, setShowProjectsPanel] = useState(false)
  const [showChannelsPanel, setShowChannelsPanel] = useState(false)

  // The URL — not component state — is the source of truth for which
  // conversation is open. Previously `activeId` was plain useState and
  // ?conversation= was read once on mount, so the address bar said "/chat"
  // forever no matter which thread you were reading: there was nothing to copy
  // and nothing to share. Deriving it from the query string means every
  // selection produces a permalink, and Back/Forward walk the threads you
  // visited.
  const [searchParams, setSearchParams] = useSearchParams()
  const activeId = searchParams.get('conversation')
  // On a phone the list and the thread are two SCREENS rather than two panes: `activeId` is
  // what decides which one you are looking at. Chat is a two-pane layout at heart and a 288px
  // list beside a thread leaves ~124px for the thread on a 412px screen, which is what made
  // the surface unusable (reported with a screenshot, 2026-10-11).
  const isNarrow = useIsNarrow()
  const showList = !isNarrow || !activeId
  const showThread = !isNarrow || !!activeId

  const setActiveId = useCallback(
    (id: string | null, opts: { replace?: boolean } = {}) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev)
          if (id) next.set('conversation', id)
          else next.delete('conversation')
          return next
        },
        // Selecting is a navigation (Back should return to the previous
        // thread); clearing after a delete is not — replace it so Back can't
        // land on a conversation that no longer exists.
        { replace: opts.replace ?? false },
      )
    },
    [setSearchParams],
  )

  const [creating, setCreating] = useState(false)

  // Fetch projects on mount.
  useEffect(() => {
    api.listProjects().then(list => setProjects(list)).catch(() => {})
  }, [])

  // Fetch conversation list whenever search/sort/filters change.
  useEffect(() => {
    let cancelled = false
    setLoadingList(true)
    setListError(null)

    api.listConversations({
      q: search || undefined,
      sort,
      kind: filterKind || undefined,
      project_id: filterProjectId || undefined,
      creator: filterCreator || undefined,
      tag: filterTag || undefined,
    })
      .then(list => {
        if (!cancelled) {
          setConversations(Array.isArray(list) ? list : [])
          setLoadingList(false)
        }
      })
      .catch(err => {
        if (!cancelled) {
          if (err instanceof UnauthorizedError) {
            setListError('Session expired. Please re-authenticate.')
          } else {
            setListError(err instanceof Error ? err.message : 'Failed to load conversations.')
          }
          setLoadingList(false)
        }
      })

    return () => { cancelled = true }
  }, [search, sort, filterKind, filterProjectId, filterCreator, filterTag])

  const handleNewChat = useCallback(async () => {
    setCreating(true)
    try {
      const conv = await api.createConversation({})
      setConversations(prev => [conv, ...prev])
      setActiveId(conv.id)
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to create conversation.', 'error')
    } finally {
      setCreating(false)
    }
  }, [setActiveId, addToast])

  const handleArchive = useCallback(async (id: string) => {
    try {
      await api.updateConversation(id, { archived: true })
      setConversations(prev =>
        prev.map(c =>
          c.id === id ? { ...c, archived_at: new Date().toISOString() } : c,
        ),
      )
      addToast('Conversation archived.', 'success')
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Archive failed.', 'error')
    }
  }, [addToast])

  const handleDelete = useCallback(async (id: string) => {
    if (!window.confirm('Permanently delete this conversation and all its messages?')) return
    try {
      await api.deleteConversation(id)
      setConversations(prev => prev.filter(c => c.id !== id))
      if (activeId === id) setActiveId(null, { replace: true })
      addToast('Conversation deleted.', 'success')
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Delete failed.', 'error')
    }
  }, [activeId, setActiveId, addToast])

  const handleRename = useCallback(async (id: string, title: string) => {
    try {
      await api.updateConversation(id, { title })
      setConversations(prev => prev.map(c => (c.id === id ? { ...c, title } : c)))
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Rename failed.', 'error')
    }
  }, [addToast])

  const handleConversationUpdated = useCallback((conv: Conversation) => {
    setConversations(prev => {
      const existing = prev.find(c => c.id === conv.id)
      if (existing) {
        return prev.map(c => (c.id === conv.id ? { ...c, ...conv } : c))
      }
      // New conversation (e.g. branch) — prepend it to the list.
      return [conv, ...prev]
    })
  }, [])

  const handleBranchCreated = useCallback((conv: Conversation) => {
    setConversations(prev => {
      if (prev.find(c => c.id === conv.id)) return prev
      return [conv, ...prev]
    })
    setActiveId(conv.id)
  }, [setActiveId])

  const handleTagClick = useCallback((tag: string) => {
    setFilterTag(prev => prev === tag ? '' : tag)
  }, [])

  return (
    /* The negative margins cancel `<main>`'s padding so Chat can run edge to edge. They have
       to match what main actually has AT THIS WIDTH — main is `px-3 py-4 md:px-6 md:py-6`, so a
       flat `-mx-6` overhangs a phone by 12px a side and pushes the composer off the bottom. */
    <div className="flex flex-col h-full -mx-3 -my-4 md:-mx-6 md:-my-6">
      {/* Page header. Hidden on a phone once a thread is open: the thread has its own header
          with the title and the way back, and a native chat app gives the conversation the
          whole screen. The page title, the lede and the three buttons were costing ~110px of
          a ~700px viewport on every message. */}
      {(!isNarrow || !activeId) && (
      <div className="px-3 py-2.5 md:px-6 md:py-4 border-b border-gray-200 bg-white shrink-0 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="text-lg md:text-2xl font-semibold text-gray-900">Chat</h1>
            <UnreadMentionsBadge />
          </div>
          {/* The lede wrapped to three lines in a 412px row beside the buttons. */}
          <p className="hidden md:block text-sm text-gray-500 mt-0.5">Conversations with fleet agents.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowProjectsPanel(true)}
            className="px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            Projects
          </button>
          <button
            onClick={() => setShowChannelsPanel(true)}
            className="px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            Channels
          </button>
          <button
            onClick={() => { void handleNewChat() }}
            disabled={creating}
            className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {creating ? 'Creating…' : <><span className="md:hidden">+</span><span className="hidden md:inline">+ New chat</span></>}
          </button>
        </div>
      </div>
      )}

      {/* Body: list pane + thread pane */}
      <div className="flex flex-1 min-h-0">
        {/* Conversation list pane. 288px beside a thread is right on a desktop and is the whole
            problem on a phone, so there it becomes the full-width "inbox" screen and steps
            aside entirely once a conversation is open. */}
        {showList && (
        <div className={`${showThread ? 'w-72 shrink-0 border-r border-gray-200' : 'w-full'} bg-white flex flex-col min-h-0`}>
          {/* Search + sort + filters */}
          <div className="px-3 pt-3 pb-2 space-y-2 border-b border-gray-100">
            <input
              type="search"
              placeholder="Search conversations…"
              value={searchRaw}
              onChange={e => setSearchRaw(e.target.value)}
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-1.5 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent"
            />
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400">Sort:</span>
              {(['activity', 'created', 'name'] as const).map(s => (
                <button
                  key={s}
                  onClick={() => setSort(s)}
                  className={`text-xs px-2 py-0.5 rounded transition-colors ${
                    sort === s
                      ? 'bg-indigo-100 text-indigo-700 font-medium'
                      : 'text-gray-500 hover:bg-gray-100'
                  }`}
                >
                  {s === 'activity' ? 'Activity' : s === 'created' ? 'Created' : 'Name'}
                </button>
              ))}
            </div>
            {/* Kind filter */}
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-gray-400 shrink-0">Kind:</span>
              {(['', 'chat', 'channel'] as const).map(k => (
                <button
                  key={k || 'all'}
                  onClick={() => setFilterKind(k)}
                  className={`text-xs px-2 py-0.5 rounded transition-colors ${
                    filterKind === k
                      ? 'bg-indigo-100 text-indigo-700 font-medium'
                      : 'text-gray-500 hover:bg-gray-100'
                  }`}
                >
                  {k === '' ? 'All' : k === 'chat' ? 'Chats' : 'Channels'}
                </button>
              ))}
            </div>
            {/* Project filter */}
            {projects.length > 0 && (
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-400 shrink-0">Project:</span>
                <select
                  value={filterProjectId}
                  onChange={e => setFilterProjectId(e.target.value)}
                  className="flex-1 text-xs border border-gray-200 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                >
                  <option value="">All</option>
                  {projects.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
            )}
            {/* Creator filter */}
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-gray-400 shrink-0">Creator:</span>
              <input
                type="text"
                value={filterCreatorRaw}
                onChange={e => setFilterCreatorRaw(e.target.value)}
                placeholder="email…"
                className="flex-1 text-xs border border-gray-200 rounded px-1.5 py-0.5 placeholder-gray-300 focus:outline-none focus:ring-1 focus:ring-indigo-400"
              />
            </div>
            {/* Active tag filter badge */}
            {filterTag && (
              <div className="flex items-center gap-1">
                <span className="text-xs text-gray-400">Tag:</span>
                <span className="inline-flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700">
                  {filterTag}
                  <button
                    onClick={() => setFilterTag('')}
                    className="text-indigo-400 hover:text-red-500 ml-0.5 leading-none"
                    title="Clear tag filter"
                  >
                    ×
                  </button>
                </span>
              </div>
            )}
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto px-2 py-2 space-y-1">
            {loadingList && (
              <p className="text-xs text-gray-400 text-center py-6 animate-pulse">Loading…</p>
            )}

            {listError && (
              <div className="mx-1 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                {listError}
              </div>
            )}

            {!loadingList && !listError && conversations.length === 0 && (
              <p className="text-xs text-gray-400 text-center py-6">
                {searchRaw ? 'No conversations match.' : 'No conversations yet.'}
              </p>
            )}

            {!loadingList && !listError && conversations.map(conv => (
              <ConvRow
                key={conv.id}
                conv={conv}
                active={conv.id === activeId}
                projects={projects}
                onSelect={() => setActiveId(conv.id)}
                onArchive={() => { void handleArchive(conv.id) }}
                onDelete={() => { void handleDelete(conv.id) }}
                onRename={title => { void handleRename(conv.id, title) }}
                onTagClick={handleTagClick}
              />
            ))}
          </div>
        </div>
        )}

        {/* Thread pane */}
        {showThread && (
        <div className="flex-1 flex flex-col min-h-0 min-w-0">
          {activeId ? (
            <ThreadPane
              key={activeId}
              conversationId={activeId}
              projects={projects}
              onConversationUpdated={handleConversationUpdated}
              onTagClick={handleTagClick}
              onBranchCreated={handleBranchCreated}
              // Only on a phone: on a desktop the list is already beside the thread, and a
              // back button there would be a control that undoes nothing visible.
              onBack={isNarrow ? () => setActiveId(null) : undefined}
            />
          ) : (
            <NoSelectionPane />
          )}
        </div>
        )}
      </div>

      {/* Projects panel modal */}
      {showProjectsPanel && (
        <ProjectsPanel
          projects={projects}
          onClose={() => setShowProjectsPanel(false)}
          onProjectsChanged={setProjects}
        />
      )}

      {/* Channels panel modal */}
      {showChannelsPanel && (
        <ChannelsPanel
          onClose={() => setShowChannelsPanel(false)}
        />
      )}

      <ToastContainer toasts={toasts} />
    </div>
  )
}
